import clsx from "clsx";
import {
  AlertTriangle,
  Building2,
  ExternalLink,
  Flame,
  Info,
  ShieldAlert,
  Wind,
  Zap,
} from "lucide-react";
import { NextSeo } from "next-seo";
import { ReactElement, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { Banner } from "~/components/Banner";
import { MainLayout } from "~/components/layouts/MainLayout";
import { MarginLayout } from "~/components/layouts/MarginLayout";
import { NextPageWithLayout } from "~/pages/_app";
import legislationDataRaw from "~/legislacja-przegladinstalacji.json";

interface LegalBasisItem {
  name: string;
  article: string;
  description: string;
  url: string;
}

interface Subcategory {
  id: string;
  name: string;
  frequency: string;
  obligatedParties: string;
  scope: string[];
  qualifiedPersons?: string;
  penalties?: string;
  legalBasis: LegalBasisItem[];
  importantNotes?: string[];
}

interface Category {
  id: string;
  name: string;
  description: string;
  subcategories: Subcategory[];
}

const data = legislationDataRaw as {
  meta: { title: string; description: string; lastUpdated: string };
  categories: Category[];
  commonLegalBasis: Array<{
    name: string;
    description: string;
    url: string;
    keyArticles?: string[];
  }>;
  sanctionsOverview: {
    title: string;
    content: string;
    consequences: Array<{ type: string; description: string; legalBasis: string }>;
  };
  frequencySummary: {
    title: string;
    items: Array<{ inspection: string; frequency: string; legalBasis: string }>;
  };
};

const categoryIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  elektryczne: Zap,
  gazowe: Flame,
  "kominiarskie-wentylacyjne": Wind,
  przeciwpozarowe: ShieldAlert,
  ogolnobudowlane: Building2,
};

function SubcategoryAccordion({ sub }: { sub: Subcategory }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={clsx(
          "group flex w-full items-start justify-between gap-3 px-4 py-3 text-left transition-colors",
          open ? "bg-sky-50" : "hover:bg-slate-50"
        )}
        aria-expanded={open}
      >
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 sm:text-base">{sub.name}</p>
          <p className="mt-0.5 text-xs font-medium text-sky-700">{sub.frequency}</p>
        </div>
        <ChevronDownIcon
          className={clsx(
            "mt-1 h-5 w-5 flex-shrink-0 text-gray-400 transition-transform duration-200",
            open && "rotate-180"
          )}
        />
      </button>

      <div
        className={clsx(
          "grid overflow-hidden transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]",
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        )}
        aria-hidden={!open}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="space-y-4 border-t border-slate-200 px-4 py-4 text-sm">
            <div>
              <span className="font-semibold text-gray-700">Zobowiązani: </span>
              <span className="text-gray-600">{sub.obligatedParties}</span>
            </div>

            <div>
              <p className="mb-1.5 font-semibold text-gray-700">Zakres kontroli:</p>
              <ul className="space-y-1.5 text-gray-600">
                {sub.scope.map((s, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>

            {sub.qualifiedPersons && (
              <div>
                <span className="font-semibold text-gray-700">Uprawnione osoby: </span>
                <span className="text-gray-600">{sub.qualifiedPersons}</span>
              </div>
            )}

            {sub.penalties && (
              <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                <div>
                  <span className="font-semibold text-amber-800">Kary: </span>
                  <span className="text-amber-700">{sub.penalties}</span>
                </div>
              </div>
            )}

            <div>
              <p className="mb-2 font-semibold text-gray-700">Podstawy prawne:</p>
              <div className="space-y-2">
                {sub.legalBasis.map((lb, i) => (
                  <div
                    key={i}
                    className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-gray-800">
                          {lb.name}
                          {lb.article && (
                            <span className="ml-1 font-semibold text-sky-700">
                              ({lb.article})
                            </span>
                          )}
                        </p>
                        <p className="mt-0.5 text-xs text-gray-500">{lb.description}</p>
                      </div>
                      <a
                        href={lb.url}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="flex-shrink-0 text-sky-600 hover:text-sky-800"
                        aria-label={`Otwórz ${lb.name}`}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {sub.importantNotes && sub.importantNotes.length > 0 && (
              <div className="flex gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
                <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" />
                <div>
                  <p className="mb-1 font-semibold text-blue-800">Ważne uwagi:</p>
                  <ul className="space-y-1.5">
                    {sub.importantNotes.map((note, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-xs text-blue-700">
                        <span className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-blue-600" />
                        {note}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function CategorySection({ category }: { category: Category }) {
  const [open, setOpen] = useState(false);
  const Icon = categoryIcons[category.id] ?? Building2;

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 shadow-sm">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={clsx(
          "group flex w-full items-center gap-4 px-5 py-4 text-left transition-colors duration-200",
          open ? "bg-sky-900 text-white" : "bg-white hover:bg-sky-900"
        )}
        aria-expanded={open}
      >
        <div
          className={clsx(
            "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border transition-colors",
            open
              ? "border-sky-700 bg-sky-800 text-white"
              : "border-sky-100 bg-sky-50 text-sky-700 group-hover:border-sky-700 group-hover:bg-sky-800 group-hover:text-white"
          )}
        >
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p
            className={clsx(
              "text-base font-semibold transition-colors sm:text-lg",
              open ? "text-white" : "text-gray-900 group-hover:text-white"
            )}
          >
            {category.name}
          </p>
          <p
            className={clsx(
              "line-clamp-2 text-sm transition-colors",
              open ? "text-sky-200" : "text-gray-500 group-hover:text-sky-200"
            )}
          >
            {category.description}
          </p>
        </div>
        <ChevronDownIcon
          className={clsx(
            "h-6 w-6 flex-shrink-0 transition-all duration-300",
            open ? "rotate-180 text-white" : "text-gray-400 group-hover:text-white"
          )}
        />
      </button>

      <div
        className={clsx(
          "grid overflow-hidden transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]",
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        )}
        aria-hidden={!open}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="space-y-2 bg-slate-50 px-4 py-4">
            {category.subcategories.map((sub) => (
              <SubcategoryAccordion key={sub.id} sub={sub} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const LegislacjaPage: NextPageWithLayout = () => {
  return (
    <>
      <Banner title={data.meta.title} />

      <MarginLayout className="flex-col sm:mt-8">
        <p className="whitespace-nowrap text-gray-600">{data.meta.description}</p>
        <p className="mt-1 text-xs text-gray-400">
          Ostatnia aktualizacja: {data.meta.lastUpdated}
        </p>
      </MarginLayout>

      {/* Frequency summary table */}
      <MarginLayout className="flex-col sm:mt-6">
        <h2 className="mb-4 text-xl font-bold text-gray-900 sm:text-2xl">
          {data.frequencySummary.title}
        </h2>
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full min-w-[560px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-sky-900 text-left text-white">
                <th className="px-4 py-3 font-semibold">Rodzaj kontroli</th>
                <th className="px-4 py-3 font-semibold">Częstotliwość</th>
                <th className="px-4 py-3 font-semibold">Podstawa prawna</th>
              </tr>
            </thead>
            <tbody>
              {data.frequencySummary.items.map((item, i) => (
                <tr
                  key={i}
                  className={clsx(
                    "border-b border-slate-100 transition-colors hover:bg-sky-50",
                    i % 2 === 0 ? "bg-white" : "bg-slate-50"
                  )}
                >
                  <td className="px-4 py-2.5 font-medium text-gray-800">{item.inspection}</td>
                  <td className="px-4 py-2.5 text-sky-700">{item.frequency}</td>
                  <td className="px-4 py-2.5 text-gray-500">{item.legalBasis}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </MarginLayout>

      {/* Category accordions */}
      <MarginLayout className="flex-col sm:mt-8">
        <h2 className="mb-4 text-xl font-bold text-gray-900 sm:text-2xl">
          Szczegółowe podstawy prawne według kategorii
        </h2>
        <div className="space-y-3">
          {data.categories.map((category) => (
            <CategorySection key={category.id} category={category} />
          ))}
        </div>
      </MarginLayout>

      {/* Sanctions overview */}
      <div className="mt-12 bg-slate-50">
        <MarginLayout className="flex-col">
          <h2 className="mb-2 text-xl font-bold text-gray-900 sm:text-2xl">
            {data.sanctionsOverview.title}
          </h2>
          <p className="mb-6 max-w-3xl text-gray-600">{data.sanctionsOverview.content}</p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.sanctionsOverview.consequences.map((c, i) => (
              <div
                key={i}
                className="rounded-xl border border-red-100 bg-white p-4 shadow-sm"
              >
                <div className="mb-2 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 text-red-500" />
                  <span className="font-semibold text-gray-900">{c.type}</span>
                </div>
                <p className="mb-2 text-sm text-gray-600">{c.description}</p>
                <p className="text-xs text-gray-400">{c.legalBasis}</p>
              </div>
            ))}
          </div>
        </MarginLayout>
      </div>

      {/* Common legal basis */}
      <MarginLayout className="mb-12 flex-col sm:mt-8">
        <h2 className="mb-4 text-xl font-bold text-gray-900 sm:text-2xl">
          Kluczowe akty prawne
        </h2>
        <div className="space-y-3">
          {data.commonLegalBasis.map((act, i) => (
            <div
              key={i}
              className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-900">{act.name}</p>
                  <p className="mt-1 text-sm text-gray-600">{act.description}</p>
                  {act.keyArticles && act.keyArticles.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {act.keyArticles.map((article, j) => (
                        <span
                          key={j}
                          className="rounded-full border border-sky-100 bg-sky-50 px-2.5 py-0.5 text-xs text-sky-700"
                        >
                          {article}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <a
                  href={act.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="flex flex-shrink-0 items-center gap-1 text-sm text-sky-600 hover:text-sky-800"
                  aria-label={`Otwórz ${act.name}`}
                >
                  <ExternalLink className="h-4 w-4" />
                  <span className="hidden sm:inline">Źródło</span>
                </a>
              </div>
            </div>
          ))}
        </div>
      </MarginLayout>
    </>
  );
};

LegislacjaPage.getLayout = function GetLayout(page: ReactElement) {
  return (
    <MainLayout>
      <NextSeo
        title={data.meta.title}
        description={data.meta.description}
      />
      {page}
    </MainLayout>
  );
};

export default LegislacjaPage;
